import { z } from "zod";

import { describeLocation, type ValidationLabels } from "../utils/validationMessages";

// Shared HTTP helpers for the API services.
//
// ApiError keeps the HTTP status next to the readable message, so callers can tell
// "not signed in" (401) and "not allowed" (403) from an ordinary failure without parsing text.

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const errorDetailSchema = z.union([
  z.string(),
  z.array(
    z
      .object({
        loc: z.array(z.union([z.string(), z.number()])),
        msg: z.string(),
      })
      .passthrough(),
  ),
]);

const errorResponseSchema = z.object({ detail: errorDetailSchema });

// pydantic prefixes messages raised by validators; the prefix says nothing to a user.
const VALIDATOR_PREFIX = /^Value error, /;

/** The server's own message when there is one, otherwise the fallback with the status. */
export function describeApiError(data: unknown, status: number, fallback: string): string {
  const parsed = errorResponseSchema.safeParse(data);

  if (!parsed.success) {
    return `${fallback} (HTTP ${status}).`;
  }

  if (typeof parsed.data.detail === "string") {
    return parsed.data.detail;
  }

  return parsed.data.detail.map((item) => item.msg.replace(VALIDATOR_PREFIX, "")).join(" ");
}

// FastAPI heads a request-body path with its own markers - "body", and "study" for the
// endpoints that wrap a study in their request - which say nothing to a user.
const PATH_MARKERS = ["body", "study"];

function fieldPath(loc: ReadonlyArray<string | number>): ReadonlyArray<string | number> {
  let start = 0;
  while (start < loc.length && loc[start] === PATH_MARKERS[start]) {
    start += 1;
  }
  return loc.slice(start);
}

/**
 * The server's own message with the field it belongs to named the way a form names it:
 * "Source 1 — Current contribution ka: Input should be greater than 0", never the raw path
 * "body.sources.0.current_contribution_ka: ...". A caller that holds its form's label map
 * passes it and the field then reads exactly as it does on the form.
 *
 * A detail that is one string is the backend's own sentence and is returned unchanged; an item
 * whose path is the request itself (`["body", "study"]`, a model validator) keeps its message
 * alone, because there is no field to name.
 */
export function describeApiFieldError(
  data: unknown,
  status: number,
  fallback: string,
  labels?: ValidationLabels,
): string {
  const parsed = errorResponseSchema.safeParse(data);

  if (!parsed.success) {
    return `${fallback} (HTTP ${status}).`;
  }

  if (typeof parsed.data.detail === "string") {
    return parsed.data.detail;
  }

  return parsed.data.detail
    .map((item) => {
      const message = item.msg.replace(VALIDATOR_PREFIX, "");
      const field = describeLocation(fieldPath(item.loc), labels);
      return field ? `${field}: ${message}` : message;
    })
    .join("; ");
}

type JsonRequest = {
  method: "GET" | "POST" | "PATCH";
  body?: unknown;
  signal?: AbortSignal | undefined;
  timeoutMs?: number;
};

export type JsonResponse = {
  status: number;
  ok: boolean;
  data: unknown;
};

/** One same-origin JSON request; the session cookie travels with it automatically. */
export async function requestJson(url: string, request: JsonRequest): Promise<JsonResponse> {
  const timeoutSignal = AbortSignal.timeout(request.timeoutMs ?? 30_000);
  const signal = request.signal ? AbortSignal.any([request.signal, timeoutSignal]) : timeoutSignal;
  const hasBody = request.body !== undefined;

  const response = await fetch(url, {
    method: request.method,
    headers: hasBody
      ? { Accept: "application/json", "Content-Type": "application/json" }
      : { Accept: "application/json" },
    ...(hasBody ? { body: JSON.stringify(request.body) } : {}),
    cache: "no-store",
    credentials: "same-origin",
    signal,
  });

  const data: unknown = await response.json().catch(() => null);

  return { status: response.status, ok: response.ok, data };
}
